import axios from "axios";
import { Feature } from "ol";
import { EsriJSON } from "ol/format";
import { Geometry } from "ol/geom";
import { config, tourismTypesToShow } from "./config";
import { coordinatesConverter, EpsgCode } from "@geo/js";
import {
    CustomSubtype,
    CustomSubtypeOIDGap,
    FeatureType,
    GamePointStatus,
    PlaceSubtype,
    SelectedStatus,
    TrackType
} from "./enums";

const params = {
    f: "json",
    returnGeometry: true,
    where: "1=1",
    outFields: "*",
    outSR: "3857"
};

const esrijsonFormat = new EsriJSON();

export const getAllFeatures = async (): Promise<Feature<Geometry>[]> => {
    return [].concat(
        ...(await Promise.all([
            getFeatures(config.objects.url + "/0/query"),
            getFeatures(config.objects.url + "/1/query", CustomSubtype.Dizkoks),
            getFeatures(config.objects.url + "/2/query", CustomSubtype.Peldvieta),
            getFeatures(config.objects.url + "/3/query", CustomSubtype.Pilskalns),
            getFeatures(config.municipalityObjects.url + "/4/query", CustomSubtype.PasvaldibasObjekts),
            getFeatures(config.infraObjects.url + "/5/query")
        ]))
    );
};

export const getFeatures = async (url: string, customSubtype?: CustomSubtype): Promise<Feature<Geometry>[]> => {
    try {
        const response = await axios.get(url, { params });
        if (!response || !response.data || response.data.error) {
            throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
        }
        customSubtype && changeOIDField(response.data, customSubtype);
        const parsed = esrijsonFormat.readFeatures(response.data);
        const features = parsed.filter((f) => f.getGeometry().getCoordinates() && f);
        fixData(features, customSubtype);
        addCoordinates(features);
        return sort(features);
    } catch (err) {
        // console.log(err);
        return [];
    }
};

export const getAllRoutes = (): Promise<Feature<Geometry>[]> => {
    return Promise.all([
        getRouteLayer(config.routes.url + "/0/query", FeatureType.RouteMainPoint),
        getRouteLayer(config.routes.url + "/1/query", FeatureType.RouteOtherPlace),
        getRouteLayer(config.routes.url + "/2/query", FeatureType.RoutePoint),
        getRouteLayer(config.routes.url + "/3/query", FeatureType.RouteTrack)
    ]);
};

const getRouteLayer = async (url: string, featureType: FeatureType) => {
    try {
        const response = await axios.get(url, { params });
        if (!response || !response.data || response.data.error) {
            throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
        }
        const parsed = esrijsonFormat.readFeatures(response.data);
        const features = parsed.filter((f) => f.getGeometry().getCoordinates() && f);
        fixRouteData(features, featureType);
        (featureType === FeatureType.RoutePoint || featureType === FeatureType.RouteOtherPlace) &&
            addCoordinates(features);
        return features;
    } catch (err) {
        // console.log(err);
        return [];
    }
};

export const getAllGames = (): Promise<Feature<Geometry>[]> => {
    return Promise.all([
        getGameLayer(config.game.url + "/0/query", FeatureType.GameMainPoint),
        getGameLayer(config.game.url + "/1/query", FeatureType.GameCheckPoint),
        getGameLayer(config.game.url + "/2/query", FeatureType.GameTrack)
    ]);
};

const getGameLayer = async (url: string, featureType: FeatureType) => {
    try {
        const response = await axios.get(url, { params });
        if (!response || !response.data || response.data.error) {
            throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
        }
        const features = esrijsonFormat.readFeatures(response.data);
        fixGameData(features, featureType);
        featureType === FeatureType.GameCheckPoint && addCoordinates(features);
        return features;
    } catch (err) {
        // console.error(err);
        return [];
    }
};

const fixGameData = (features: Feature[], featureType: FeatureType) => {
    features.map((feature) =>
        feature.setProperties(
            {
                GAMEPOINTTYPE: featureType,
                SELECTED: SelectedStatus.Default,
                GAMEPOINTSTATUS: GamePointStatus.Default,
                HIDDEN: false
            },
            true
        )
    );
};

export const getAllTracks = async (): Promise<Feature<Geometry>[]> => {
    return [].concat(
        ...(await Promise.all(
            Object.values(TrackType).map((val) => getTrackLayer(config.tracks.url + "/" + val + "/query", Number(val)))
        ))
    );
};

const getTrackLayer = async (url: string, layerNr: number) => {
    try {
        const response = await axios.get(url, { params });
        if (!response || !response.data || response.data.error) {
            throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
        }
        const features = esrijsonFormat.readFeatures(response.data);
        fixTrackData(features, layerNr);
        return features;
    } catch (err) {
        // console.error(err);
        return [];
    }
};

const fixTrackData = (features: Feature[], layerNr: number) => {
    features.map((feature) =>
        feature.setProperties(
            {
                TRACKTYPE: layerNr,
                SELECTED: SelectedStatus.Default
            },
            true
        )
    );
};

const fixData = (features: Feature[], customSubtype?: CustomSubtype) => {
    for (const feature of features) {
        const properties = {
            VISITED: 0,
            LIKE: 0,
            ROUTE: 0,
            SELECTED: 0,
            FEATURETYPE: FeatureType.LVMRecreationPlace
        };

        if (customSubtype) {
            customSubtype !== CustomSubtype.PasvaldibasObjekts &&
                (properties["PLACESUBTYPE"] =
                    customSubtype === CustomSubtype.Dizkoks
                        ? PlaceSubtype.DabasApskatesObjekts
                        : customSubtype === CustomSubtype.Peldvieta
                        ? PlaceSubtype.AtputasVieta
                        : customSubtype === CustomSubtype.Pilskalns
                        ? PlaceSubtype.KulturvesturisksApskatesObjekts
                        : null);

            properties["CUSTOMSUBTYPE"] = customSubtype;

            const placename = feature.get("NAMEMAMMADABA") || feature.get("TITLE") || feature.get("NAME");
            placename && (properties["PLACENAME"] = placename);

            const placeDescription = feature.get("DESCRIPTIONMAMMADABA") || feature.get("DESCRIPTION");
            placeDescription && (properties["PLACEDESCRIPTION"] = placeDescription);

            !feature.get("URL") && (properties["URL"] = feature.get("PERMALINK"));
        }

        const tourismTypes = feature.get("TOURISMTYPESSTR");
        tourismTypes &&
            (properties["TOURISMTYPESSTR"] = tourismTypes
                .split(";")
                .filter((i) => tourismTypesToShow.includes(i))
                .join(";"));

        feature.setProperties(properties);
    }
};

const fixRouteData = (features: Feature[], featureType: FeatureType) => {
    for (const feature of features) {
        const name = feature.get("NAME");
        if (name) {
            feature.set("PLACENAME", name, true);
            feature.unset("NAME", true);
        }
        const description = feature.get("DESCRIPTION");
        if (description) {
            feature.set("PLACEDESCRIPTION", description, true);
            feature.unset("DESCRIPTION", true);
        }
        if (featureType === FeatureType.RouteMainPoint) {
            feature.set("PLACESUBTYPE", 40);
        }
        feature.setProperties({ SELECTED: 0, ROUTE: 0, FEATURETYPE: featureType }, true);
    }
};

const addCoordinates = (features: Feature[]) => {
    for (const feature of features) {
        const wgs84 = coordinatesConverter.convert(
            feature.getGeometry().getCoordinates(),
            EpsgCode.WebMercator,
            EpsgCode.Wgs84
        );
        const lks92 = coordinatesConverter.convert(
            feature.getGeometry().getCoordinates(),
            EpsgCode.WebMercator,
            EpsgCode.Lks92
        );

        feature.setProperties({ WGS84: wgs84, LKS92: lks92 });
    }
};

const sort = (features: Feature[]) => {
    if (!features.length) {
        return [];
    }
    const mammadabaFeatures = [];
    const otherFeatures = [];
    for (const feature of features) {
        feature.get("PLACESUBTYPE") === 7 ? mammadabaFeatures.push(feature) : otherFeatures.push(feature);
    }
    return mammadabaFeatures.concat(otherFeatures);
};

const changeOIDField = (data, customSubtype: CustomSubtype): void =>
    data.features.map(
        (feature) =>
            (feature.attributes.OBJECTID +=
                customSubtype === CustomSubtype.Dizkoks
                    ? CustomSubtypeOIDGap.Dizkoks
                    : customSubtype === CustomSubtype.Peldvieta
                    ? CustomSubtypeOIDGap.Peldvieta
                    : customSubtype === CustomSubtype.Pilskalns
                    ? CustomSubtypeOIDGap.Pilskalns
                    : customSubtype === CustomSubtype.PasvaldibasObjekts
                    ? CustomSubtypeOIDGap.PasvaldibasObjekts
                    : 0)
    );
